"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { Bookmark, BookmarkCheck, Heart, PlayCircle } from "lucide-react";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { enroll, likeModule, saveModule } from "@/lib/lecturehub/module-actions";
import { ShareActions } from "@/components/ui/ShareActions";
import type { SharePayload } from "./moduleShare";
import type { Access } from "./paywall";
import { AccessCta, AccessPrice, ctaClass, ctaStyle } from "./AccessCta";

type Row = {
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  value: string;
};

type Props = {
  lang: Lang;
  moduleId: number;
  /** `moduleAccess(...)` for this reader (./paywall.ts). */
  access: Access;
  /** Opens without paying (`isFreeToUnlock`) — the "Free" price line. */
  free: boolean;
  isAuthed: boolean;
  initialLiked: boolean;
  initialSaved: boolean;
  /** Anchor href that smooth-scrolls to the player on the page. */
  playAnchor?: string;
  /** null/absent → no share (PRIVATE portal, or caller opted out). */
  share?: SharePayload | null;
  /** "Watch now" / "Listen now" caption. */
  playLabel?: string;
  /** The one-tap caption for a PREMIUM item marked free (kind "start"), per
   *  media type ("Klikni in poslušaj", "Klikni in glej"). */
  freeLabel?: string;
  /**
   * When true, suppress the "play" CTA even though the user can view.
   * Use this when the actual media player is already rendered visibly
   * elsewhere on the page (e.g. PodcastView puts the player in the
   * About card), so the sidebar CTA would be a redundant anchor link.
   * The locked item's CTA still shows.
   */
  hidePlayCta?: boolean;
  /** Show the "Save" action alongside "Favorites". Default true; OTT detail
   *  passes false so it shows Favorites only (matches CourseStartCard). */
  showSave?: boolean;
  rows?: Row[];
};

export function MediaSidebar({
  lang,
  moduleId,
  access,
  free,
  isAuthed,
  initialLiked,
  initialSaved,
  playAnchor = "#player",
  share = null,
  playLabel,
  freeLabel,
  hidePlayCta = false,
  showSave = true,
  rows = [],
}: Props) {
  const t = useDict();
  const pt = t.portal;
  const router = useRouter();
  const [liked, setLiked] = useState(initialLiked);
  const [saved, setSaved] = useState(initialSaved);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function loginUrl(): string {
    return `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`;
  }

  // A PREMIUM item marked free (kind "start") is served on a free enrolment;
  // the one tap makes it and the refresh brings the media in. Every other
  // video or podcast plays the moment `canView` is true — nobody enrols.
  function start() {
    setError(null);
    startTransition(async () => {
      const r = await enroll(moduleId);
      if (r.ok) router.refresh();
      else setError(r.error ?? null);
    });
  }

  function toggleLike() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !liked;
    setLiked(next);
    startTransition(async () => {
      const r = await likeModule(moduleId, next);
      if (!r.ok) {
        setLiked(!next);
        setError(r.error ?? null);
      }
    });
  }

  function toggleSave() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !saved;
    setSaved(next);
    startTransition(async () => {
      const r = await saveModule(moduleId, next);
      if (!r.ok) {
        setSaved(!next);
        setError(r.error ?? null);
      }
    });
  }

  // The play CTA for an open item is an anchor to the player on the page —
  // or nothing, where the player is already in view (hidePlayCta).
  const cta =
    access.kind === "open" && hidePlayCta ? null : (
      <AccessCta
        access={access}
        lang={lang}
        moduleId={moduleId}
        open={
          <a href={playAnchor} className={ctaClass} style={ctaStyle}>
            <PlayCircle className="size-4" aria-hidden />
            {playLabel ?? t.detail.watchNow}
          </a>
        }
        onStart={start}
        startLabel={freeLabel ?? t.detail.clickToOpen}
        pending={pending}
      />
    );

  // Like/Save come in two shapes so this shared card matches CourseStartCard
  // when a primary CTA is present (compact icon circle to the CTA's right),
  // yet still degrades to labelled full-width buttons when the CTA is
  // suppressed (podcast — the player is rendered inline, so `cta` is null).
  const circleBtn =
    "grid size-12 shrink-0 place-items-center rounded-full border transition-colors";
  const circleOn = "border-accent/30 bg-accent/15 text-accent";
  const circleOff =
    "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground";
  const wideBtn =
    "flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full text-[12px] font-display font-bold uppercase tracking-[0.12em] transition-colors";
  const wideOn = "bg-accent/15 text-foreground";
  const wideOff =
    "border border-border/60 text-foreground/75 hover:bg-foreground/[0.04] hover:text-foreground";

  const likeCircle = (
    <button
      type="button"
      onClick={toggleLike}
      aria-pressed={liked}
      aria-label={liked ? t.detail.liked : t.detail.like}
      title={liked ? t.detail.liked : t.detail.like}
      className={cn(circleBtn, liked ? circleOn : circleOff)}
    >
      <Heart className={cn("size-5", liked ? "fill-current" : "")} aria-hidden />
    </button>
  );

  const saveCircle = showSave ? (
    <button
      type="button"
      onClick={toggleSave}
      aria-pressed={saved}
      aria-label={saved ? t.detail.saved : t.detail.save}
      title={saved ? t.detail.saved : t.detail.save}
      className={cn(circleBtn, saved ? circleOn : circleOff)}
    >
      {saved ? (
        <BookmarkCheck className="size-5" aria-hidden />
      ) : (
        <Bookmark className="size-5" aria-hidden />
      )}
    </button>
  ) : null;

  const likeWide = (
    <button
      type="button"
      onClick={toggleLike}
      aria-pressed={liked}
      className={cn(wideBtn, liked ? wideOn : wideOff)}
    >
      <Heart className={cn("size-3.5", liked ? "fill-current text-accent" : "")} aria-hidden />
      {liked ? t.detail.liked : t.detail.like}
    </button>
  );

  const saveWide = showSave ? (
    <button
      type="button"
      onClick={toggleSave}
      aria-pressed={saved}
      className={cn(wideBtn, saved ? wideOn : wideOff)}
    >
      {saved ? (
        <BookmarkCheck className="size-3.5 text-accent" aria-hidden />
      ) : (
        <Bookmark className="size-3.5" aria-hidden />
      )}
      {saved ? t.detail.saved : t.detail.save}
    </button>
  ) : null;

  return (
    // Sticky lives on the column wrapper in the parent detail client (so
    // any future sibling cards pin together with this one), not the inner
    // aside — see CourseStartCard for the same pattern.
    <aside className="media-sidebar">
      <div className="bg-card text-card-foreground rounded-card border border-border/60 p-6 md:p-7 overflow-hidden">
        {/* Price block — prominent, mirrors CourseStartCard's price treatment. */}
        <div className="media-sidebar__price">
          <AccessPrice access={access} free={free} />
        </div>

        {/* Auth-gate note: whenever a signed-out visitor hits a gate, explain it
            and offer register alongside the login CTA below — the shared gate
            copy (portal.authGateBodyTpl) with {login}/{register} link slots,
            the same template PaywallCard uses. */}
        {access.kind === "login" ? (
          <p className="mt-3 text-[12.5px] leading-relaxed text-foreground/70">
            {pt.authGateBodyTpl.split(/(\{login\}|\{register\})/).map((part, i) =>
              part === "{login}" ? (
                <Link
                  key={i}
                  href={langPath(lang, "/login")}
                  className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {pt.authGateLoginWord}
                </Link>
              ) : part === "{register}" ? (
                <Link
                  key={i}
                  href={langPath(lang, "/register")}
                  className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {pt.authGateRegisterWord}
                </Link>
              ) : (
                part
              ),
            )}
          </p>
        ) : null}

        {/* CTA + Like — Like sits as a compact circle to the right of the
            primary CTA (CourseStartCard layout). When the play CTA is
            suppressed (hidePlayCta), the actions keep the full-width form. */}
        {cta ? (
          <div className="mt-4 flex items-center gap-2">
            <div className="min-w-0 flex-1">{cta}</div>
            {likeCircle}
            {saveCircle}
            {share ? (
              <ShareActions
                variant="circle"
                url={share.url}
                title={share.title}
                text={share.text}
              />
            ) : null}
          </div>
        ) : (
          <div className={cn("mt-4 grid gap-2", showSave ? "grid-cols-2" : "grid-cols-1")}>
            {likeWide}
            {saveWide}
          </div>
        )}

        {rows.length > 0 ? (
          <dl className="mt-6 grid gap-2.5 text-[12.5px]">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <r.Icon className="size-3.5 text-foreground/40 shrink-0" aria-hidden />
                <dt className="text-foreground/60 flex-1">{r.label}</dt>
                <dd className="font-display font-semibold text-foreground tabular-nums">
                  {r.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {error ? <p className="text-xs text-destructive mt-3">{error}</p> : null}
      </div>
    </aside>
  );
}
